// Solicitudes ARCO — the compliance file for what people have asked us to do
// with their data.
//
// One component, two mounts: the operator console (working a client's file) and
// the clinic's own Settings, exactly as ConsentSettings is mounted.
//
// The copy takes one position throughout and never softens it: the clinic is
// the RESPONSABLE. We process patient data on their instruction as encargado,
// so logging a request here does not discharge their duty and the banner says
// so. Getting that wrong would be worse than not building the screen, because
// it would let someone believe they had complied.
//
// The deadline is the whole point of the screen. It is stored on the row, and
// shown as days remaining rather than a bare date, because "vence el 7 de
// septiembre" requires the reader to do the arithmetic that tells them whether
// to act today.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import {
  downloadExport,
  eraseTenantRequest,
  fetchTenantExport,
  searchTenantSubjects,
  type SubjectCandidate,
} from './privacy-export.js'
import {
  ARCO_RIGHTS,
  type ArcoRight,
  type ArcoState,
  type CreatePrivacyRequest,
  createTenantPrivacyRequest,
  deadlineText,
  getTenantPrivacyRequests,
  type PrivacyRequest,
  OPPOSITION_HELP,
  OPPOSITION_KINDS,
  OPPOSITION_LABEL,
  RIGHT_HELP,
  RIGHT_LABEL,
  shortDate,
  STATE_LABEL,
  type StateFilter,
  updateTenantPrivacyRequest,
  type UpdatePrivacyRequest,
  VIA_LABEL,
} from './privacy-requests-data.js'
import {
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  type DataTableColumn,
  Drawer,
  FormField,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
} from '../../design-system/index.js'

export interface PrivacyRequestsProps {
  /**
   * The client whose compliance file this is. REQUIRED — this panel only ever
   * mounts in the operator console. Requests arrive at privacidad@inherta.com,
   * which only INHERTA can read, so only INHERTA can act on one.
   */
  tenantId: string
}

/** Open states map to a chip that draws the eye; closed ones stay quiet. */
const STATE_TONE: Record<ArcoState, 'ok' | 'warn' | 'sens' | 'info' | 'draft'> = {
  received: 'info',
  awaiting_identity: 'warn',
  routed_to_tenant: 'info',
  in_progress: 'info',
  fulfilled: 'ok',
  refused: 'draft',
  expired: 'draft',
}

const FILTERS: { value: StateFilter; label: string }[] = [
  { value: 'abiertas', label: 'Abiertas' },
  { value: 'todas', label: 'Todas' },
  { value: 'received', label: 'Recibidas' },
  { value: 'awaiting_identity', label: 'Falta identificación' },
  { value: 'routed_to_tenant', label: 'Turnadas a la clínica' },
  { value: 'in_progress', label: 'En proceso' },
  { value: 'fulfilled', label: 'Atendidas' },
]

interface NewDraft {
  rights: ArcoRight[]
  subjectReference: string
  subjectNote: string
  receivedVia: 'email' | 'chat' | 'phone' | 'other'
  receivedAt: string
}

const EMPTY_DRAFT: NewDraft = {
  rights: [],
  subjectReference: '',
  subjectNote: '',
  receivedVia: 'email',
  receivedAt: '',
}

/** `<input type="date">` gives `2026-08-08`; the API wants a full instant. */
function toIsoOrUndefined(day: string): string | undefined {
  if (day === '') return undefined
  const d = new Date(`${day}T12:00:00`)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

function todayLocal(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/**
 * A client's Solicitudes ARCO compliance file: list + deadline chips, new-request
 * modal and per-request drawer (link subject, export, erase, oposición). Mounts
 * only in the operator console — INHERTA receives every request.
 */
export function PrivacyRequests({ tenantId }: PrivacyRequestsProps) {
  const qc = useQueryClient()
  const [filter, setFilter] = useState<StateFilter>('abiertas')
  const [newOpen, setNewOpen] = useState(false)
  const [draft, setDraft] = useState<NewDraft>(EMPTY_DRAFT)
  const [formError, setFormError] = useState<string | null>(null)
  const [selected, setSelected] = useState<PrivacyRequest | null>(null)

  const queryKey = ['privacy-requests', tenantId, filter]
  const query = useQuery({
    queryKey,
    queryFn: () => getTenantPrivacyRequests(tenantId, filter),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['privacy-requests', tenantId] })

  const create = useMutation({
    mutationFn: (body: CreatePrivacyRequest) => createTenantPrivacyRequest(tenantId, body),
    onSuccess: async () => {
      setNewOpen(false)
      setDraft(EMPTY_DRAFT)
      setFormError(null)
      await invalidate()
    },
    onError: () => setFormError('No se pudo registrar la solicitud. Intenta de nuevo.'),
  })

  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdatePrivacyRequest }) =>
      updateTenantPrivacyRequest(tenantId, id, patch),
    onSuccess: async (updated) => {
      setSelected(updated)
      await invalidate()
    },
  })

  function submitNew() {
    if (draft.rights.length === 0) {
      setFormError('Elige al menos un derecho.')
      return
    }
    if (draft.subjectReference.trim() === '') {
      setFormError('Escribe con qué dato vas a localizar a la persona.')
      return
    }
    const receivedAt = toIsoOrUndefined(draft.receivedAt)
    create.mutate({
      rights: draft.rights,
      subjectReference: draft.subjectReference.trim(),
      ...(draft.subjectNote.trim() !== '' ? { subjectNote: draft.subjectNote.trim() } : {}),
      receivedVia: draft.receivedVia,
      ...(receivedAt ? { receivedAt } : {}),
    })
  }

  const columns: DataTableColumn<PrivacyRequest>[] = [
    {
      key: 'subjectReference',
      label: 'Persona',
      render: (r) => (
        <div>
          <div className="font-medium text-text-1">{r.subjectReference}</div>
          <div className="text-[11px] text-text-3">
            {VIA_LABEL[r.receivedVia] ?? r.receivedVia} · {shortDate(r.receivedAt)}
          </div>
        </div>
      ),
    },
    {
      key: 'rights',
      label: 'Derechos',
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.rights.map((right) => (
            <Chip key={right} tone="draft">
              {RIGHT_LABEL[right]}
            </Chip>
          ))}
        </div>
      ),
    },
    {
      key: 'state',
      label: 'Estado',
      render: (r) => <Chip tone={STATE_TONE[r.state]}>{STATE_LABEL[r.state]}</Chip>,
    },
    {
      key: 'respondBy',
      label: 'Plazo',
      render: (r) => {
        // A closed request has no deadline left to miss, so showing one would
        // be noise that trains people to ignore the column.
        if (r.closed) return <span className="text-text-3">—</span>
        const pending = r.respondedAt === null
        const iso = pending ? r.respondBy : (r.executeBy ?? r.respondBy)
        const { text, tone } = deadlineText(iso)
        return (
          <div>
            <div
              className={
                tone === 'danger'
                  ? 'font-semibold text-sens'
                  : tone === 'warning'
                    ? 'font-medium text-warn'
                    : 'text-text-2'
              }
            >
              {text}
            </div>
            <div className="text-[11px] text-text-3">
              {pending ? 'para responder' : 'para ejecutar'} · {shortDate(iso)}
            </div>
          </div>
        )
      },
    },
  ]

  return (
    <div className="space-y-5">
      <Banner tone="info">
        La clínica es la responsable de los datos de sus pacientes. Nosotros los tratamos como
        encargados, por instrucción suya. Registrar aquí una solicitud deja constancia y marca el
        plazo, pero la obligación de responder al titular sigue siendo de la clínica.
      </Banner>

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-text-1">Solicitudes ARCO</h3>
            <p className="mt-1 max-w-2xl text-sm text-text-2">
              Cuando alguien pide acceder, corregir, eliminar u oponerse al uso de sus datos,
              regístralo aquí. El aviso de privacidad promete responder en 20 días hábiles y
              ejecutar en 15 más, y esos plazos se calculan desde el día en que llegó la solicitud.
            </p>
          </div>
          <Button onClick={() => setNewOpen(true)}>Nueva solicitud</Button>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <FormField label="Ver" htmlFor="privacy-filter">
            <Select
              id="privacy-filter"
              value={filter}
              options={FILTERS.map((f) => ({ value: f.value, label: f.label }))}
              onChange={(e) => setFilter(e.target.value as StateFilter)}
            />
          </FormField>
          {query.data ? (
            <p className="pt-6 text-sm text-text-3">
              {query.data.open === 0
                ? 'Ninguna solicitud abierta.'
                : query.data.open === 1
                  ? '1 solicitud abierta.'
                  : `${query.data.open} solicitudes abiertas.`}
            </p>
          ) : null}
        </div>

        <div className="mt-4">
          {query.isError ? (
            <Banner tone="sens">
              No se pudieron cargar las solicitudes. Recarga la página para intentar de nuevo.
            </Banner>
          ) : (
            <DataTable
              caption="Solicitudes ARCO"
              columns={columns}
              rows={query.data?.requests ?? []}
              loading={query.isLoading}
              rowActions={(row) => [
                { id: 'open', label: 'Ver solicitud', onSelect: () => setSelected(row) },
              ]}
              empty={
                <div className="py-8 text-center">
                  <p className="text-sm font-medium text-text-1">
                    {filter === 'abiertas'
                      ? 'No hay solicitudes abiertas.'
                      : 'No hay solicitudes con este filtro.'}
                  </p>
                  <p className="mt-1 text-sm text-text-2">
                    Las solicitudes llegan por correo a privacidad@inherta.com. Regístralas aquí en
                    cuanto lleguen para que el plazo empiece a contar desde el día correcto.
                  </p>
                </div>
              }
            />
          )}
        </div>
      </Card>

      <NewRequestModal
        open={newOpen}
        busy={create.isPending}
        draft={draft}
        error={formError}
        onChange={setDraft}
        onClose={() => {
          setNewOpen(false)
          setFormError(null)
        }}
        onSubmit={submitNew}
      />

      <RequestDrawer
        // Keyed on the request so opening a different one starts clean. Without
        // it the drawer keeps its state, and the resolution typed for one
        // person would be sitting in the box when the next one is opened.
        key={selected?.id ?? 'none'}
        request={selected}
        busy={update.isPending}
        tenantId={tenantId}
        onClose={() => setSelected(null)}
        onPatch={(patch) => {
          if (selected) update.mutate({ id: selected.id, patch })
        }}
        onErased={() => {
          setSelected(null)
          void invalidate()
        }}
      />
    </div>
  )
}

// ── Logging one that arrived ────────────────────────────────────────────────

function NewRequestModal({
  open,
  busy,
  draft,
  error,
  onChange,
  onClose,
  onSubmit,
}: {
  open: boolean
  busy: boolean
  draft: NewDraft
  error: string | null
  onChange: (d: NewDraft) => void
  onClose: () => void
  onSubmit: () => void
}) {
  const toggleRight = (right: ArcoRight) => {
    onChange({
      ...draft,
      rights: draft.rights.includes(right)
        ? draft.rights.filter((r) => r !== right)
        : [...draft.rights, right],
    })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      title="Registrar una solicitud"
      subtitle="Anota lo que pidió la persona y cuándo llegó. El plazo se calcula desde esa fecha."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={onSubmit} disabled={busy}>
            {busy ? 'Registrando…' : 'Registrar'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error ? <Banner tone="sens">{error}</Banner> : null}

        <fieldset>
          <legend className="text-sm font-medium text-text-1">¿Qué pidió?</legend>
          <p className="mt-1 text-[13px] text-text-3">
            Puedes marcar más de uno. Es común pedir los datos y luego su eliminación.
          </p>
          <div className="mt-2 space-y-2">
            {ARCO_RIGHTS.map((right) => (
              <label
                key={right}
                className="flex cursor-pointer items-start gap-2 rounded-sm border border-line px-3 py-2 hover:bg-field"
              >
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={draft.rights.includes(right)}
                  onChange={() => toggleRight(right)}
                />
                <span>
                  <span className="block text-sm font-medium text-text-1">
                    {RIGHT_LABEL[right]}
                  </span>
                  <span className="block text-[13px] text-text-2">{RIGHT_HELP[right]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <FormField
          label="¿Con qué dato la vas a localizar?"
          htmlFor="subject-reference"
          helper="El teléfono, el usuario de Instagram, el correo o el nombre que dio la persona."
        >
          <Input
            id="subject-reference"
            value={draft.subjectReference}
            onChange={(e) => onChange({ ...draft, subjectReference: e.target.value })}
            placeholder="+52 155 1234 5678"
            maxLength={255}
          />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField label="¿Por dónde llegó?" htmlFor="received-via">
            <Select
              id="received-via"
              value={draft.receivedVia}
              options={[
                { value: 'email', label: 'Correo' },
                { value: 'chat', label: 'Chat' },
                { value: 'phone', label: 'Teléfono' },
                { value: 'other', label: 'Otro' },
              ]}
              onChange={(e) =>
                onChange({ ...draft, receivedVia: e.target.value as NewDraft['receivedVia'] })
              }
            />
          </FormField>

          <FormField
            label="¿Qué día llegó?"
            htmlFor="received-at"
            helper="Si la registras días después, pon la fecha real. El plazo corre desde entonces."
          >
            <Input
              id="received-at"
              type="date"
              max={todayLocal()}
              value={draft.receivedAt}
              onChange={(e) => onChange({ ...draft, receivedAt: e.target.value })}
            />
          </FormField>
        </div>

        <FormField
          label="Notas"
          htmlFor="subject-note"
          helper="Opcional. Por ejemplo, qué identificación adjuntó."
        >
          <Textarea
            id="subject-note"
            value={draft.subjectNote}
            onChange={(e) => onChange({ ...draft, subjectNote: e.target.value })}
            maxLength={4000}
          />
        </FormField>
      </div>
    </Modal>
  )
}

// ── Working one ─────────────────────────────────────────────────────────────

function RequestDrawer({
  request,
  busy,
  tenantId,
  onClose,
  onPatch,
  onErased,
}: {
  request: PrivacyRequest | null
  busy: boolean
  tenantId: string
  onClose: () => void
  onPatch: (patch: UpdatePrivacyRequest) => void
  onErased: () => void
}) {
  const [resolution, setResolution] = useState('')

  if (!request) return null

  const pending = request.respondedAt === null
  const deadlineIso = pending ? request.respondBy : (request.executeBy ?? request.respondBy)
  const deadline = deadlineText(deadlineIso)

  return (
    // 420 rather than the 300 default: this holds a two-column summary, a
    // textarea and two buttons. `max-w-[calc(100vw-2rem)]` inside Drawer caps
    // it on a phone.
    <Drawer open={request !== null} onClose={onClose} title="Solicitud ARCO" width={420}>
      <div className="space-y-5">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-text-3">Persona</p>
          <p className="text-sm text-text-1">{request.subjectReference}</p>
          {request.subjectNote ? (
            <p className="mt-1 whitespace-pre-wrap text-[13px] text-text-2">
              {request.subjectNote}
            </p>
          ) : null}
        </div>

        <div className="flex flex-wrap gap-2">
          {request.rights.map((r) => (
            <Chip key={r} tone="draft">
              {RIGHT_LABEL[r]}
            </Chip>
          ))}
          <Chip tone={STATE_TONE[request.state]}>{STATE_LABEL[request.state]}</Chip>
        </div>

        <SubjectSection
          request={request}
          tenantId={tenantId}
          busy={busy}
          onPatch={onPatch}
          onErased={onErased}
        />

        {request.closed ? (
          <Banner tone="ok">
            Cerrada el {shortDate(request.executedAt ?? request.respondedAt ?? request.createdAt)}.
          </Banner>
        ) : (
          <Banner tone={deadline.tone === 'neutral' ? 'info' : 'sens'}>
            {deadline.text} {pending ? 'para responder al titular' : 'para ejecutar lo prometido'}.
            Vence el {shortDate(deadlineIso)}.
          </Banner>
        )}

        <dl className="grid grid-cols-2 gap-3 text-[13px]">
          <div>
            <dt className="text-text-3">Llegó</dt>
            <dd className="text-text-1">{shortDate(request.receivedAt)}</dd>
          </div>
          <div>
            <dt className="text-text-3">Por</dt>
            <dd className="text-text-1">{VIA_LABEL[request.receivedVia] ?? request.receivedVia}</dd>
          </div>
          <div>
            <dt className="text-text-3">Respondida</dt>
            <dd className="text-text-1">
              {request.respondedAt ? shortDate(request.respondedAt) : 'Todavía no'}
            </dd>
          </div>
          <div>
            <dt className="text-text-3">Ejecutada</dt>
            <dd className="text-text-1">
              {request.executedAt ? shortDate(request.executedAt) : 'Todavía no'}
            </dd>
          </div>
        </dl>

        {request.resolution ? (
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-text-3">
              Resolución
            </p>
            <p className="whitespace-pre-wrap text-sm text-text-1">{request.resolution}</p>
          </div>
        ) : null}

        {request.closed ? null : (
          <div className="space-y-3 border-t border-line pt-4">
            <FormField
              label="¿Qué decidiste?"
              htmlFor="resolution"
              helper="Queda en el expediente. Es lo que le mostrarías a la autoridad si preguntara."
            >
              <Textarea
                id="resolution"
                value={resolution}
                onChange={(e) => setResolution(e.target.value)}
                maxLength={8000}
                placeholder="Procede. Se le enviaron sus datos por correo el 12 de agosto."
              />
            </FormField>

            <div className="flex flex-wrap gap-2">
              {pending ? (
                <>
                  <Button
                    disabled={busy}
                    onClick={() =>
                      onPatch({
                        markResponded: true,
                        ...(resolution.trim() !== '' ? { resolution: resolution.trim() } : {}),
                      })
                    }
                  >
                    Registrar respuesta
                  </Button>
                  <Button
                    variant="ghost"
                    disabled={busy || resolution.trim() === ''}
                    onClick={() =>
                      onPatch({
                        markResponded: true,
                        state: 'refused',
                        resolution: resolution.trim(),
                      })
                    }
                  >
                    Marcar improcedente
                  </Button>
                </>
              ) : (
                <Button disabled={busy} onClick={() => onPatch({ markExecuted: true })}>
                  Marcar como ejecutada
                </Button>
              )}
              {busy ? <Spinner /> : null}
            </div>
            {pending ? (
              <p className="text-[13px] text-text-3">
                Marcar improcedente requiere escribir el motivo. La ley permite negarse en casos
                concretos, y ese motivo es justo lo que hay que poder mostrar después.
              </p>
            ) : null}

            {/* The steps BEFORE answering. Without these the filter offered
                "Falta identificación" while nothing could ever put a request
                in that state, and the encargado routing the aviso promises in
                writing had no button at all. */}
            {pending ? (
              <div className="border-t border-line pt-3">
                <p className="text-[13px] text-text-2">
                  ¿Todavía no puedes responder? Deja constancia de en qué va. El plazo sigue
                  corriendo.
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {request.state !== 'awaiting_identity' ? (
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => onPatch({ state: 'awaiting_identity' })}
                    >
                      Le pedimos identificación
                    </Button>
                  ) : null}
                  {tenantId && request.state !== 'routed_to_tenant' ? (
                    // Only on the operator mount: a clinic is the responsable
                    // and would never route a request to itself.
                    <Button
                      variant="secondary"
                      disabled={busy}
                      onClick={() => onPatch({ state: 'routed_to_tenant' })}
                    >
                      Turnar a la clínica
                    </Button>
                  ) : null}
                  <Button
                    variant="ghost"
                    disabled={busy || resolution.trim() === ''}
                    onClick={() =>
                      onPatch({
                        markResponded: true,
                        state: 'expired',
                        resolution: resolution.trim(),
                      })
                    }
                  >
                    Nunca se identificó
                  </Button>
                </div>
                <p className="mt-2 text-[13px] text-text-3">
                  {request.state === 'awaiting_identity'
                    ? 'Esperando su identificación. '
                    : request.state === 'routed_to_tenant'
                      ? 'Turnada a la clínica. '
                      : ''}
                  Cerrar por falta de identificación también requiere escribir el motivo.
                </p>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </Drawer>
  )
}

// ── Acceso: identify the person, then hand over their file ──────────────────

/**
 * Two states, and they are a sequence rather than a choice: nobody is linked
 * yet, so search; or someone is linked, so the export is available.
 *
 * The search is deliberately a confirm-then-link step rather than a one-click
 * "export whoever matches". A phone number is not an identity — numbers get
 * reassigned, families share a handset — and exporting the wrong patient's
 * file to whoever wrote in is the worst thing this screen could do.
 */
function SubjectSection({
  request,
  tenantId,
  busy,
  onPatch,
  onErased,
}: {
  request: PrivacyRequest
  tenantId: string
  busy: boolean
  onPatch: (patch: UpdatePrivacyRequest) => void
  onErased: () => void
}) {
  // Seeded with what the person wrote in with: usually the right search.
  const [term, setTerm] = useState(request.subjectReference)
  const [searching, setSearching] = useState(false)
  const [results, setResults] = useState<SubjectCandidate[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [confirmText, setConfirmText] = useState('')
  const [erasing, setErasing] = useState(false)

  async function search() {
    if (term.trim().length < 2) {
      setError('Escribe al menos dos caracteres.')
      return
    }
    setSearching(true)
    setError(null)
    try {
      setResults(await searchTenantSubjects(tenantId, term.trim()))
    } catch {
      setError('No se pudo buscar. Intenta de nuevo.')
    } finally {
      setSearching(false)
    }
  }

  async function download() {
    setDownloading(true)
    setError(null)
    try {
      const data = await fetchTenantExport(tenantId, request.id)
      downloadExport(data, request.subjectReference)
    } catch {
      setError('No se pudo generar el archivo. Intenta de nuevo.')
    } finally {
      setDownloading(false)
    }
  }

  async function erase() {
    setErasing(true)
    setError(null)
    try {
      await eraseTenantRequest(tenantId, request.id, confirmText)
      setConfirmOpen(false)
      setConfirmText('')
      // The request is now fulfilled and the person scrubbed, so the drawer
      // and the list both have to reload rather than keep showing the old row.
      onErased()
    } catch {
      setError('No se pudieron eliminar los datos. No se borró nada.')
    } finally {
      setErasing(false)
    }
  }

  if (request.contactId) {
    return (
      <div className="space-y-3 rounded-sm border border-line p-3">
        <div className="flex items-center justify-between gap-2">
          <p className="text-[13px] font-medium text-text-1">Persona identificada</p>
          <Button variant="ghost" disabled={busy} onClick={() => onPatch({ contactId: null })}>
            Cambiar
          </Button>
        </div>
        <p className="text-[13px] text-text-2">
          Descarga dos archivos: uno para leer y otro en formato JSON para trasladar los datos a
          otro proveedor.
        </p>
        {error ? <Banner tone="sens">{error}</Banner> : null}
        <Button disabled={downloading} onClick={() => void download()}>
          {downloading ? 'Preparando…' : 'Descargar sus datos'}
        </Button>

        {/* Oposición sits with the identified person because that is who it
            belongs to: the objection outlives the letter that raised it, and
            it keeps applying long after this request is closed. */}
        <div className="border-t border-line pt-3">
          <p className="text-[13px] font-medium text-text-1">A qué se opone</p>
          <p className="mt-1 text-[13px] text-text-2">
            Se aplica de inmediato y se queda aplicado, aunque cierres la solicitud.
          </p>
          <div className="mt-2 space-y-2">
            {OPPOSITION_KINDS.map((kind) => (
              <label key={kind} className="flex cursor-pointer items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-1"
                  disabled={busy}
                  checked={request.contactOpposition.includes(kind)}
                  onChange={(e) =>
                    onPatch({
                      opposition: e.target.checked
                        ? [...request.contactOpposition, kind]
                        : request.contactOpposition.filter((k) => k !== kind),
                    })
                  }
                />
                <span>
                  <span className="block text-[13px] text-text-1">{OPPOSITION_LABEL[kind]}</span>
                  <span className="block text-[12px] text-text-2">{OPPOSITION_HELP[kind]}</span>
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* Rectificación happens on the person's own record, where the fields
            live. Linking there rather than duplicating an edit form keeps one
            place where a name is corrected. */}
        <div className="border-t border-line pt-3">
          <p className="text-[13px] text-text-2">
            Para corregir un dato equivocado, edítalo en su ficha y luego marca la solicitud como
            ejecutada.
          </p>
          <p className="mt-1 text-[13px] text-text-3">
            Desde aquí no se edita la ficha: /contacts/:id resuelve en NUESTRO tenant, no en el de
            la clínica. Pídeles que corrijan el dato en Contactos y cierra la solicitud cuando lo
            confirmen.
          </p>
        </div>

        {/* Erasure is offered only where it was asked for. Putting an
            irreversible button on a request that never invoked cancelación
            invites doing it by accident. */}
        {request.rights.includes('cancelacion') && !request.closed ? (
          <div className="border-t border-line pt-3">
            <p className="text-[13px] text-text-2">
              Eliminar sus datos es permanente. Se conservan el registro de consentimiento y la
              bitácora, sin el contenido de las conversaciones, porque la ley obliga a guardarlos.
            </p>
            <Button variant="destructive" className="mt-2" onClick={() => setConfirmOpen(true)}>
              Eliminar sus datos
            </Button>
          </div>
        ) : null}

        <EraseModal
          open={confirmOpen}
          busy={erasing}
          reference={request.subjectReference}
          typed={confirmText}
          onType={setConfirmText}
          onClose={() => {
            setConfirmOpen(false)
            setConfirmText('')
          }}
          onConfirm={() => void erase()}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3 rounded-sm border border-line p-3">
      <p className="text-[13px] font-medium text-text-1">¿De quién es esta solicitud?</p>
      <p className="text-[13px] text-text-2">
        Busca por teléfono, usuario de Instagram, correo o nombre. Confirma que es la persona
        correcta antes de vincularla: es lo que autoriza entregar o borrar su expediente.
      </p>
      {error ? <Banner tone="sens">{error}</Banner> : null}
      <div className="flex gap-2">
        <Input
          value={term}
          aria-label="Buscar a la persona"
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void search()
          }}
        />
        <Button variant="ghost" disabled={searching} onClick={() => void search()}>
          {searching ? 'Buscando…' : 'Buscar'}
        </Button>
      </div>

      {results !== null && results.length === 0 ? (
        <p className="text-[13px] text-text-2">
          No encontramos a nadie con ese dato. Prueba con otro: el teléfono con y sin lada, el
          usuario de Instagram, o parte del nombre. Si de verdad no tenemos nada suyo, eso también
          es una respuesta válida a la solicitud.
        </p>
      ) : null}

      {results?.map((c) => (
        <button
          key={c.contactId}
          type="button"
          disabled={busy}
          onClick={() => onPatch({ contactId: c.contactId })}
          className="block w-full rounded-sm border border-line p-2 text-left hover:bg-field disabled:opacity-50"
        >
          <span className="block text-sm font-medium text-text-1">
            {c.name ?? 'Sin nombre'}
            {c.status === 'merged' ? ' (expediente fusionado)' : ''}
          </span>
          {c.email ? <span className="block text-[12px] text-text-2">{c.email}</span> : null}
          <span className="block text-[12px] text-text-2">
            {c.identities.map((i) => `${i.channel}: ${i.externalId}`).join(' · ')}
          </span>
          <span className="block text-[11px] text-text-3">
            Coincide por {c.matchedOn.join(', ')}
            {c.lastSeen ? ` · último contacto ${shortDate(c.lastSeen)}` : ''}
          </span>
        </button>
      ))}
    </div>
  )
}

/**
 * Tier A destructive confirmation, the shape manage-tenant.ts uses for
 * deleting a tenant: the action stays disabled until the exact reference is
 * typed, and what was typed is echoed to the server, which re-checks it
 * against the row. A confirmation only the browser enforces is one an API
 * client skips.
 */
function EraseModal({
  open,
  busy,
  reference,
  typed,
  onType,
  onClose,
  onConfirm,
}: {
  open: boolean
  busy: boolean
  reference: string
  typed: string
  onType: (v: string) => void
  onClose: () => void
  onConfirm: () => void
}) {
  const matches = typed.trim() === reference.trim()
  return (
    <Modal
      open={open}
      onClose={onClose}
      busy={busy}
      tone="sens"
      role="alertdialog"
      title="Eliminar los datos de esta persona"
      subtitle="Esto no se puede deshacer."
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="destructive" disabled={!matches || busy} onClick={onConfirm}>
            {busy ? 'Eliminando…' : 'Eliminar definitivamente'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <p className="text-sm text-text-2">
          Se borran su nombre, teléfono, correo, notas, etiquetas y el contenido de sus
          conversaciones. Sus citas conservan la fecha pero pierden sus datos.
        </p>
        <p className="text-sm text-text-2">
          Se conservan el registro de consentimiento y la bitácora de auditoría, sin el contenido de
          las conversaciones, porque la ley obliga a guardarlos.
        </p>
        <FormField
          label={`Escribe "${reference}" para confirmar`}
          htmlFor="erase-confirm"
          helper="Tal cual aparece en la solicitud."
        >
          <Input
            id="erase-confirm"
            value={typed}
            autoComplete="off"
            onChange={(e) => onType(e.target.value)}
          />
        </FormField>
      </div>
    </Modal>
  )
}
